// Time-phrase parser for cooking-step text.
//
// Detects durations like:
//   "10 minutes", "10 min", "10m"
//   "1 hour", "1 hr", "1h"
//   "2 hours 30 minutes", "1h 15min"
//   "for 20 minutes"
//   "until ~ 5-7 minutes"  → picks the upper bound (7)
//   "30 seconds", "45 s"
//
// Returns an array of { label, seconds, start, end } so the UI can
// render one chip per detected duration in step order. The `start`/
// `end` indices let us highlight the substring in the rendered step
// if we ever want to (we don't right now — chips render separately
// below the step text).
//
// Stays conservative: anything we don't confidently recognize is
// skipped. Better to under-detect than to schedule a 3-hour alarm
// because the step said "page 30 in your cookbook".

export interface StepDuration {
  label: string;      // verbatim text we matched, e.g. "30 minutes"
  seconds: number;    // resolved duration in seconds
  start: number;      // index in source string where match begins
  end: number;        // index in source string where match ends (exclusive)
}

// Word + abbreviation forms for each unit. Ordered longest-first
// inside each group so "minutes" wins over "min".
const UNIT_PATTERN =
  "(?:hours?|hrs?|h(?![a-z])" +
  "|minutes?|mins?|m(?![a-z])" +
  "|seconds?|secs?|s(?![a-z]))";

// One number, optionally followed by a range continuation
// ("5-7 minutes", "5 to 7 minutes"). We pick the upper bound so the
// timer covers the longer end (better to over-cook briefly than to
// pull a roast at the optimistic minimum).
const NUMBER = "(\\d+(?:\\.\\d+)?)";
const RANGE = `(?:\\s*[-–—]\\s*${NUMBER}|\\s+to\\s+${NUMBER})?`;
const UNIT_MATCHER = new RegExp(
  `${NUMBER}${RANGE}\\s*(${UNIT_PATTERN})\\b`,
  "gi"
);

function unitSeconds(unit: string): number {
  const u = unit.toLowerCase();
  if (u.startsWith("h")) return 3600;
  if (u.startsWith("s")) return 1;
  // anything else in our pattern is a minute variant
  return 60;
}

export function findStepDurations(text: string): StepDuration[] {
  if (!text) return [];
  const out: StepDuration[] = [];
  // Reset regex state because /g regexes carry lastIndex across calls.
  UNIT_MATCHER.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = UNIT_MATCHER.exec(text)) !== null) {
    const lo = parseFloat(m[1]);
    const hiRange = m[2] ?? m[3];
    const hi = hiRange ? parseFloat(hiRange) : lo;
    const unit = m[4];
    if (!Number.isFinite(hi) || hi <= 0) continue;
    const secs = Math.round(hi * unitSeconds(unit));
    // Cap at 12 hours — anything longer is almost certainly not a
    // real cooking timer (e.g. "marinate overnight, 12 hours" — we'd
    // still allow it; but "1 cup at 350F" shouldn't get scheduled).
    if (secs > 12 * 3600) continue;
    if (secs < 30) continue;  // floor: 30s, anything shorter is noise
    out.push({
      label: m[0].trim(),
      seconds: secs,
      start: m.index,
      end: m.index + m[0].length,
    });
  }
  // Dedupe adjacent identical durations (e.g. "5 minutes" appearing
  // twice in the same step) — keep the first.
  return out.filter((d, i, arr) => i === 0 || d.seconds !== arr[i - 1].seconds);
}

// "30 min" / "1 hr 15 min" / "2 hr" — for chip labels. Always returns
// the shortest sensible form (we don't render "0 hr 30 min").
export function humanizeSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr} hr ${remMin} min` : `${hr} hr`;
}
